import { db } from "../firebaseClient"
import { doc, setDoc, updateDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"
import { logPlatformEvent } from "./eventHelpers"
import { issueCertificate } from "./certHelpers"

// Minimal "pending issuance" workflow, separate from certHelpers.issueCertificate
// (which issues immediately). A request sits here until an org admin approves it,
// at which point the caller issues the actual certificate and marks this "issued".
export const createCredentialRequest = async ({ orgId, userId, userName, courseId, courseTitle }) => {
  if (!orgId || !userId || !courseId) {
    return { requestId: null, error: { message: "Missing required fields" } }
  }
  try {
    const requestRef = doc(collection(db, "credentialRequests"))
    await setDoc(requestRef, {
      requestId: requestRef.id,
      orgId,
      userId,
      userName: userName || "",
      courseId,
      courseTitle: courseTitle || "",
      status: "pending", // "pending" | "issued" | "rejected"
      requestedAt: serverTimestamp(),
      resolvedAt: null,
    })
    return { requestId: requestRef.id, error: null }
  } catch (error) {
    return { requestId: null, error }
  }
}

export const resolveCredentialRequest = async (requestId, status, orgId = null) => {
  try {
    const requestRef = doc(db, "credentialRequests", requestId)
    await updateDoc(requestRef, { status, resolvedAt: serverTimestamp() })
    logPlatformEvent({
      type: status === "issued" ? "CREDENTIAL_REQUEST_APPROVED" : "CREDENTIAL_REQUEST_REJECTED",
      actorType: "org_admin",
      orgId,
      targetPath: `credentialRequests/${requestId}`,
      metadata: { requestId, status },
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Approves a pending request by actually issuing the certificate (via
// certHelpers), then marking the request resolved. If issuance fails, the
// request is left "pending" rather than silently marked issued.
export const approveCredentialRequest = async (request, { orgName, orgVerified, template } = {}) => {
  const { certificate, error: issueError } = await issueCertificate({
    userId: request.userId,
    userName: request.userName,
    courseId: request.courseId,
    courseTitle: request.courseTitle,
    orgId: request.orgId,
    orgName,
    orgVerified,
    templateId: template?.id || null,
    template,
  })
  if (issueError) return { certificate: null, error: issueError }

  const { error: resolveError } = await resolveCredentialRequest(request.requestId, "issued", request.orgId)
  if (resolveError) return { certificate, error: resolveError }

  return { certificate, error: null }
}

export const listPendingCredentialRequests = async (orgId) => {
  try {
    const requestsRef = collection(db, "credentialRequests")
    const q = query(requestsRef, where("orgId", "==", orgId), where("status", "==", "pending"))
    const snap = await getDocs(q)
    const requests = snap.docs?.map((d) => d.data())
    return { requests, error: null }
  } catch (error) {
    return { requests: [], error }
  }
}